/* Extension auth page: signs the user in and generates a token for the browser extension. */

import { useEffect, useState } from "react";
import { useAuth, useUser, SignIn } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { RoutePath } from "@/routePaths";
import { KiwiIcon } from "@/components/brand/KiwiIcon";

function buildTokenPayload(userId: string) {
  return {
    userId,
    exp: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days
  };
}

export function ExtensionAuthPage() {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const ensureUserExists = useMutation(api.auth.ensureUserExists);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const generateExtensionToken = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    setErrorMessage(null);

    try {
      const userId = await ensureUserExists();
      const token = btoa(JSON.stringify(buildTokenPayload(userId)));
      const email = user?.primaryEmailAddress?.emailAddress || "";
      const name =
        user?.fullName ||
        user?.firstName ||
        user?.primaryEmailAddress?.emailAddress ||
        "User";

      const params = new URLSearchParams({ token, email, name, userId });
      navigate(`${RoutePath.ExtensionAuthCallback}?${params.toString()}`);
    } catch (err) {
      console.error("Failed to generate extension token:", err);
      setErrorMessage("Failed to authorize extension. Please try again.");
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return;
    generateExtensionToken();
  }, [isSignedIn, isLoaded, user]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isSignedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-2xl p-8 max-w-md w-full text-center border border-border">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <div className="w-8 h-8 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">
            Authorizing Extension
          </h1>
          <p className="text-muted-foreground mb-4">
            Please wait while we authorize your browser extension...
          </p>
          {errorMessage && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-destructive text-sm">{errorMessage}</p>
              <button
                onClick={generateExtensionToken}
                className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <KiwiIcon className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            FeedKiwi Extension
          </h1>
          <p className="text-muted-foreground">
            Sign in to connect your browser extension
          </p>
        </div>

        <div className="flex justify-center">
          <SignIn
            routing="hash"
            signUpUrl={RoutePath.SignUp}
            forceRedirectUrl={RoutePath.ExtensionAuth}
          />
        </div>
      </div>
    </div>
  );
}
